import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Spin } from 'antd';
import styled from 'styled-components';
import { Box } from 'atoms';
import { Header } from 'components';
import DeactivateButton from './DeactivateButton';
import BusinessUnitDetailsBox from './BusinessUnitDetailsBox';
import { getListAction, addUser } from 'store/pageSlice';
import BusinessUnitTabs from './BusinessUnitTabs';
import DeactivateBusinessUnitModal from './modals/DeactivateBusinessUnitModal';

const InactiveBanner = styled.div`
	position: absolute;
	width: 1100px;
	height: 48px;
	left: 0px;
	top: 0px;
	background: #ffebeb;
	flex: none;
	order: 0;
	flex-grow: 0;
	z-index: 0;
	display: flex;
	flex-direction: row;
	align-items: center;
	padding: 15px 26px;
	gap: 10px;
`;
const BannerBar = styled.div`
	position: absolute;
	width: 4px;
	height: 48px;
	left: 0px;
	top: 0px;
	background: #ca1010;
`;
const BannerText = styled.span`
	font-family: 'Inter', sans-serif;
	font-weight: 400;
	font-size: 14px;
	line-height: 17px;
	color: #ca1010;
`;

export default function BusinessUnitView() {
	const { id } = useParams();
	const dispatch = useDispatch();
	const [buData, setBuData] = useState({});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const [breadcrumb, setBreadcrumb] = useState({});

	const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
	const [deactivateLoading, setDeactivateLoading] = useState(false);
	const [buId, setBuId] = useState(null);

	const navigate = useNavigate();

	useEffect(() => {
		setLoading(true);
		setError(null);
		dispatch(
			getListAction({
				search_type: 'business_unit',
				offset: 0,
				id,
			})
		)
			.then((res) => {
				const result = res?.rows?.[0];
				if (!result) throw new Error('Business Unit not found');
				setBuData({
					buName: result.name,
					buCode: result.code,
					openFrom: result.created_at ? String(result.created_at).slice(0, 10) : '',
					status: result.is_active ? 'Active' : 'Inactive',
				});
				setBreadcrumb((prev) => ({
					...prev,
					text: `Business Unit - ${result.name || ''}`,
					link: '/business-units',
				}));
				setBuId(result.id);
				setLoading(false);
			})
			.catch((err) => {
				setError(err.message);
				setLoading(false);
			});
	}, [dispatch, id]);

	const handleDeactivate = () => {
		setDeactivateModalOpen(true);
	};

	const handleDeactivateBusinessUnit = async () => {
		setDeactivateLoading(true);
		try {
			await dispatch(
				addUser({
					data: {
						update_type: 'deactivate_business_unit',
						data: {
							bu_id: buId,
						},
					},
					isEditMode: true,
				})
			);
			setDeactivateLoading(false);
			setDeactivateModalOpen(false);
			navigate('/business-units');
		} catch (err) {
			setDeactivateLoading(false);
			// Optionally show error message
		}
	};

	let content;
	if (loading) {
		content = <Spin size="large" style={{ marginTop: 40 }} />;
	} else {
		content = <BusinessUnitDetailsBox {...buData} isInactive={buData.status === 'Inactive'} />;
	}

	if (error) {
		console.warn('Backend error:', error);
	}

	return (
		<Box p={4} style={{ position: 'relative', minHeight: 300 }}>
			{buData.status === 'Inactive' && (
				<InactiveBanner>
					<BannerBar />
					<BannerText>This Business Unit has Been Deactivated</BannerText>
				</InactiveBanner>
			)}
			{buData.status !== 'Inactive' && <DeactivateButton onDeactivate={handleDeactivate} />}
			<Header urls={breadcrumb} />
			<div
				style={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'flex-start',
					width: '100%',
					marginTop: 64,
					minHeight: 130,
					position: 'relative',
				}}
			>
				{content}
			</div>
			<Box mt={4}>
				<BusinessUnitTabs buId={buId} isInactive={buData.status === 'Inactive'} />
			</Box>
			<DeactivateBusinessUnitModal
				open={deactivateModalOpen}
				onCancel={() => setDeactivateModalOpen(false)}
				onDeactivate={handleDeactivateBusinessUnit}
				loading={deactivateLoading}
			/>
		</Box>
	);
}
